import { useContext } from 'react';

import { MAVLINK_MESSAGE_IDS as MESSAGE } from '@/define/messageId';
import { MessageContext } from '@/helpers/MessageProvider';
import useMavLinkAddress from '@/hooks/useMavLinkAddress';

const useMissionCommand = () => {
  const { publishCommand } = useContext(MessageContext);
  const { getAddress } = useMavLinkAddress();

  const clear = (missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/clear', [[...address, missionType]]);
    const topics = [`${robot.id}/telemetry/${MESSAGE.MISSION_ACK}`];

    // PX4는 missionAck만으로는 실패 시의 원인을 특정할 수 없음
    // statustext 메시지를 통해 상세한 실패 원인 정보를 제공함
    // 따라서 PX4는 statustext 토픽을 추가 구독하여 명령 실패에 대응함
    if (robot.model.maker.includes('PX4')) {
      topics.push(`${robot.id}/telemetry/${MESSAGE.STATUSTEXT}`);
    }

    return {
      execute,
      topics,
      isMatch: (data) => {
        // PX4는 statustext 토픽에서 Busy 메시지가 있으면 매칭
        if (robot.model.maker.includes('PX4') && data.text?.includes('WPM: IGN CLEAR CMD: Busy')) {
          return true;
        }

        return data.missionType === missionType;
      },
      isSuccess: (data) => {
        // isMatch에서 이미 Busy 메시지만 필터링되므로, text가 있으면 실패
        if ('text' in data) {
          return false;
        }

        return data.type === 0;
      },
    };
  };

  const requestItem = (seq, missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/request_item', [[...address, seq, missionType]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.MISSION_ITEM_INT}`,
      isMatch: (data) => data.seq === seq,
    };
  };

  const requestList = (missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/request_list', [[...address, missionType]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.MISSION_COUNT}`,
      isMatch: (data) => data.missionType === missionType,
    };
  };

  const sendAck = (robot, type, missionType) => {
    const address = getAddress(robot.id);
    publishCommand(robot, 'mission/send_ack', [[...address, type, missionType]]);
  };

  const sendCount = (count, missionType) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/send_count', [[...address, count, missionType]]);

    return {
      execute,
      topics: [
        `${robot.id}/telemetry/${MESSAGE.MISSION_REQUEST}`,
        `${robot.id}/telemetry/${MESSAGE.MISSION_REQUEST_INT}`,
      ],
      isMatch: (data) => data.missionType === missionType,
      isSuccess: (data) => data.seq === 0,
    };
  };

  const sendItem = (message, missionType, seq, totalCount) => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/send_item', [[...address, ...message, missionType]]);

    if (seq < totalCount - 1) {
      return {
        execute,
        topics: [
          `${robot.id}/telemetry/${MESSAGE.MISSION_REQUEST}`,
          `${robot.id}/telemetry/${MESSAGE.MISSION_REQUEST_INT}`,
        ],
        isSuccess: () => true,
      };
    }

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.MISSION_ACK}`,
      isMatch: (data) => data.missionType === missionType,
      isSuccess: () => true,
    };
  };

  const startArduPilot = () => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/start/ardupilot', [[...address]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 300,
      isSuccess: (data) => data.result === 0,
    };
  };

  const startPX4 = () => (robot) => {
    const address = getAddress(robot.id);
    const execute = () => publishCommand(robot, 'mission/start/px4', [[address[0]]]);

    return {
      execute,
      topic: `${robot.id}/telemetry/${MESSAGE.COMMAND_ACK}`,
      isMatch: (data) => data.command === 176,
      isSuccess: (data) => data.result === 0,
    };
  };

  return {
    clear,
    requestItem,
    requestList,
    sendAck,
    sendCount,
    sendItem,
    startArduPilot,
    startPX4,
  };
};

export default useMissionCommand;
